import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Divider, Grid, KeyValue, Link, SectionHeader, Typography } from 'web-library';
import { useHistory } from 'react-router-dom';
import RouteKey from '../../../routes/key';
import { useGetExamLevelAmendment } from '../../../api/candidate/exam/@candidate/queries-services/queries';
import { useWOGAA } from '../../../hooks/wogaa';

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(2),
  },
  submitForm: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  link: {
    color: theme.color.link.linkActive01,
  },
  iconButton: {
    padding: theme.spacing(1),
    margin: 0,
    marginLeft: theme.spacing(2),
  },
  formContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(2),

    '& .MuiFormLabel-root': {
      marginBottom: theme.spacing(2),
    },
  },
  spacing: {
    marginBottom: theme.spacing(6),
  },
  actionButton: {
    marginRight: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    marginRight: theme.spacing(2),
  },
  paragraph: {
    marginBottom: theme.spacing(1),
  },
}));

const GridKeyValue = ({ label, value }: { label: string; value: string | null }) => (
  <Grid xl={5} lg={6} md={12} sm={12} xs={12}>
    <KeyValue keyText={label} valueText={value || '-'} capitalize={false} />
  </Grid>
);

const SubjectAmendmentRegister = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const { amendment } = RouteKey.child;
  const { editRegistration, examRegistrations } = RouteKey.amendment.child;

  const { data } = useGetExamLevelAmendment();

  /** WOGAA Subject Amendment transaction tracking - fires when candidate presses Start Subject Amendment */
  const { transactionStart } = useWOGAA('myexamsseab-6452');

  return (
    <>
      <SectionHeader title="Important" />
      <ul>
        <li className={classes.paragraph}>
          <Typography variant="body1">
            {t('amendment->>SubjectAmendmentRegister.Content1')}
          </Typography>
          <ol type="a">
            <li className={classes.paragraph}>
              <Typography variant="body1">
                {t('amendment->>SubjectAmendmentRegister.Content1Sub1')}
              </Typography>
            </li>
            <li className={classes.paragraph}>
              <Typography variant="body1">
                {t('amendment->>SubjectAmendmentRegister.Content1Sub2')}
              </Typography>
            </li>
          </ol>
          <li className={classes.paragraph}>
            <Typography variant="body1">
              {t('amendment->>SubjectAmendmentRegister.Content2')}
            </Typography>
          </li>
          <li className={classes.paragraph}>
            <Typography variant="body1">
              {t('amendment->>SubjectAmendmentRegister.Content3')}
            </Typography>
          </li>
          <li className={classes.paragraph}>
            <Typography variant="body1">
              {t('amendment->>SubjectAmendmentRegister.Content4')}
            </Typography>
          </li>
        </li>
      </ul>
      <Typography className={classes.spacing} variant="body1">
        {t('amendment->>SubjectAmendmentRegister.For more information, please visit our')}{' '}
        <Link className={classes.link} href="/faq" target=" _blank" rel=" noopener,noreferrer">
          {t('exam->>SubjectAmendmentRegister.FAQ')}
        </Link>
      </Typography>
      <Divider />
      <SectionHeader title="Get Started" />
      <Typography variant="body1">
        {t(
          'amendment->>SubjectAmendmentRegister.You are about to make subject amendments for the following exam registration.'
        )}
      </Typography>
      <Grid className={classes.formContainer} container>
        {data && (
          <>
            <GridKeyValue
              label={t('exam->>RegistrationSummary.ApplicationId')}
              value={data.applicationId}
            />
            <GridKeyValue
              label={t('exam->>RegisterExamInstructions.Exam Level(s)')}
              value={data.examLevel.toString()}
            />
          </>
        )}
      </Grid>
      <Button
        id="button-resitReceipt-goToRegisteredExams"
        className={classes.button}
        onClick={() => {
          if (data) {
            /** WOGAA Subject Amendment transaction tracking start */
            transactionStart();
            history.push(
              `/${amendment}/${examRegistrations.route}/${data.uuid}/${editRegistration.route}`
            );
          }
        }}
      >
        {t('amendment->>SubjectAmendmentRegister.Start Subject Amendment')}
      </Button>
    </>
  );
};

export default SubjectAmendmentRegister;
